import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { submitContact } from '../services/api';
import { trackContact } from '../utils/metaPixel';
import { Mail, Phone, MapPin, Send, ExternalLink, Clock, Instagram, Facebook } from 'lucide-react';
import { toast } from 'sonner';
import { GYM_VENUE } from '../config/venue';
import { SOCIAL_LINKS } from '../config/social';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await submitContact(formData);
      toast.success('Üzenet sikeresen elküldve!');
      trackContact({ content_name: 'contact_form_home' });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Az üzenet küldése sikertelen. Kérlek próbáld újra.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 bg-gray-900">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <div className="inline-block mb-4 px-4 py-2 bg-yellow-600/20 border border-yellow-600/30 rounded-full">
              <span className="text-yellow-400 font-semibold text-sm tracking-wider">KAPCSOLAT</span>
            </div>
            
            <h2 
              data-testid="contact-heading"
              className="text-4xl md:text-5xl font-bold text-white mb-6"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Kezdd el az
              <span className="text-yellow-400"> átalakulást</span>
            </h2>
            
            <p className="text-lg text-gray-300 mb-8">
              Készen állsz a következő lépésre? Foglalj ingyenes konzultációt a naptáramban — személyesen a BB Gymben vagy online, ahogy neked kényelmes —
              vagy írj e-mailt, és 24 órán belül válaszolok.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-yellow-600/20 p-3 rounded-lg">
                  <Mail className="text-yellow-400" size={24} />
                </div>
                <div>
                  <div className="font-semibold text-white mb-1">E-mail</div>
                  <div className="text-gray-400" data-testid="contact-email">info@oroszcoaching.hu</div>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="bg-yellow-600/20 p-3 rounded-lg">
                  <Phone className="text-yellow-400" size={24} />
                </div>
                <div>
                  <div className="font-semibold text-white mb-1">Telefon</div>
                  <div className="text-gray-400" data-testid="contact-phone">+36 70 237-8299</div>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="bg-yellow-600/20 p-3 rounded-lg">
                  <MapPin className="text-yellow-400" size={24} />
                </div>
                <div>
                  <div className="font-semibold text-white mb-1">Edzőterem</div>
                  <div className="text-gray-300 leading-relaxed" data-testid="contact-location">
                    <span className="font-semibold text-white">{GYM_VENUE.name}</span>
                    <br />
                    <span className="text-gray-400">{GYM_VENUE.fullAddress}</span>
                  </div>
                  <a
                    href={GYM_VENUE.mapDeepLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-map-deeplink"
                    className="inline-flex items-center gap-1 mt-2 text-xs text-yellow-400 hover:text-yellow-300 transition-colors font-medium"
                  >
                    Útvonaltervezés <ExternalLink size={12} />
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-social">
                <div className="bg-yellow-600/20 p-3 rounded-lg">
                  <Instagram className="text-yellow-400" size={24} />
                </div>
                <div>
                  <div className="font-semibold text-white mb-1">Kövess a közösségi médiában</div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1">
                    <a
                      href={SOCIAL_LINKS.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-instagram-link"
                      className="inline-flex items-center gap-1.5 text-gray-400 hover:text-yellow-400 transition-colors"
                    >
                      <Instagram size={15} /> Instagram <ExternalLink size={11} />
                    </a>
                    <a
                      href={SOCIAL_LINKS.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-facebook-link"
                      className="inline-flex items-center gap-1.5 text-gray-400 hover:text-yellow-400 transition-colors"
                    >
                      <Facebook size={15} /> Facebook <ExternalLink size={11} />
                    </a>
                  </div>
                </div>
              </div>

              {/* Opening hours block — bound to the same GYM_VENUE.openingHours
                  array that populates the LocalBusiness JSON-LD schema. */}
              <div className="flex items-start gap-4" data-testid="contact-opening-hours">
                <div className="bg-yellow-600/20 p-3 rounded-lg">
                  <Clock className="text-yellow-400" size={24} />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-white mb-2">Nyitvatartás</div>
                  <ul className="space-y-1.5 text-sm">
                    {GYM_VENUE.openingHours.map((slot) => (
                      <li
                        key={slot.daysHu}
                        data-testid={`opening-hours-row-${slot.daysHu.replace(/[\s–]/g, '-').toLowerCase()}`}
                        className="flex items-center justify-between gap-4 text-gray-300 max-w-[280px]"
                      >
                        <span className="text-gray-400">{slot.daysHu}</span>
                        <span className="font-semibold text-white tabular-nums">
                          {slot.opens} – {slot.closes}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-lg border border-gray-700">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <Label htmlFor="name" className="text-white mb-2 block">Teljes név *</Label>
                <Input
                  data-testid="contact-name-input"
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500"
                  placeholder="Kovács János"
                />
              </div>
              
              <div>
                <Label htmlFor="email" className="text-white mb-2 block">E-mail cím *</Label>
                <Input
                  data-testid="contact-email-input"
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500"
                  placeholder="janos@example.com"
                />
              </div>
              
              <div>
                <Label htmlFor="phone" className="text-white mb-2 block">Telefonszám</Label>
                <Input
                  data-testid="contact-phone-input"
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500"
                  placeholder="+36 30 123 4567"
                />
              </div>
              
              <div>
                <Label htmlFor="message" className="text-white mb-2 block">Üzenet *</Label>
                <Textarea
                  data-testid="contact-message-input"
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500"
                  placeholder="Mesélj a céljaidról és tapasztalataidról…"
                />
              </div>
              
              <Button 
                data-testid="contact-submit-btn"
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold text-lg py-6 transition-all duration-300"
              >
                {isSubmitting ? 'Küldés…' : 'Üzenet küldése'}
                <Send className="ml-2" size={20} />
              </Button>
            </form>
          </div>
        </div>

        {/* Interactive gym location map — responsive, mobile-first, premium framing. */}
        <div
          data-testid="contact-map-wrapper"
          className="mt-16 rounded-2xl overflow-hidden border border-gray-700 shadow-[0_10px_40px_-15px_rgba(202,138,4,0.35)] bg-gray-800/40 backdrop-blur-sm"
        >
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-gray-700 bg-gray-900/60">
            <div className="flex items-center gap-3">
              <MapPin className="text-yellow-400" size={18} />
              <div className="text-sm text-gray-200">
                <span className="font-semibold text-white">{GYM_VENUE.name}</span>
                <span className="text-gray-400 mx-2">·</span>
                <span className="text-gray-400">{GYM_VENUE.fullAddress}</span>
              </div>
            </div>
            <a
              href={GYM_VENUE.mapDeepLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-map-open-fullscreen"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-yellow-400 hover:text-yellow-300 transition-colors"
            >
              Megnyitás Google Térképen <ExternalLink size={12} />
            </a>
          </div>
          <div className="aspect-[16/9] sm:aspect-[21/9] w-full">
            <iframe
              title={`Térkép: ${GYM_VENUE.name}`}
              data-testid="contact-map-iframe"
              src={GYM_VENUE.mapEmbedUrl}
              className="w-full h-full border-0 filter grayscale-[15%] contrast-105"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
